import * as Hammer from 'hammerjs';

export interface CarouselOptions {
	carouselEl: HTMLElement;
	carouselClass: string;
	doIndicators?: boolean;
	doPagination?: boolean;
	/*  */
	duration?: number;
	onChange?: Function;
	onNext?: Function;
	onPrev?: Function;
	onInit?: Function;
}

export default abstract class CarouselAbstr {
	public slides: Element[] = [];
	protected indicators: Element[] = [];

	protected carouselClass: string;

	public carouselEl: HTMLElement;
	protected carouselInnerEl: HTMLElement;
	protected carouselFrameEl: HTMLElement;
	protected carouselCounterEl: HTMLElement;

	protected controls: HTMLElement;
	protected controlNext: HTMLElement;
	protected controlPrev: HTMLElement;

	protected hammerManager: HammerManager;

	protected indicatorTray: HTMLElement;

	protected duration: number;
	protected timeout: number;

	public priorIndex: number; /** The index used prior to the current */
	public currentIndex: number;

	protected doIndicators: boolean;
	protected doPagination: boolean;

	protected onChange: Function;
	protected onNext: Function;
	protected onPrev: Function;
	protected onInit: Function;

	constructor(options: CarouselOptions) {
		this.carouselClass = options.carouselClass;
		this.carouselEl = options.carouselEl;

		if (!this.carouselEl) return;

		this.carouselInnerEl = this.carouselEl.getElementsByClassName(this.carouselClass + '__inner')[0] as HTMLElement;
		this.carouselFrameEl = this.carouselEl.getElementsByClassName(this.carouselClass + '__frame')[0] as HTMLElement;

		this.onChange = options.onChange;
		this.onNext = options.onNext;
		this.onPrev = options.onPrev;
		this.onInit = options.onInit;

		this.hammerManager = new Hammer(this.carouselEl as HTMLElement);

		this.duration = options.duration;
		this.doIndicators = options.doIndicators;
		this.doPagination = options.doPagination;
	}

	protected abstract init(): void;

	protected buildControls(): void {
		if (this.slides.length === 1)
			return;

		const controls: Element = document.createElement('div');
		const controlsNext: Element = document.createElement('button');
		const controlsPrev: Element = document.createElement('button');

		controls.className = `${this.carouselClass}__controls`;
		controlsNext.className = `${this.carouselClass}__control carousel--next`;
		controlsPrev.className = `${this.carouselClass}__control carousel--prev`;

		this.controlPrev = controls.appendChild(controlsPrev) as HTMLElement;
		this.controlNext = controls.appendChild(controlsNext) as HTMLElement;
		this.controls = this.carouselInnerEl.appendChild(controls) as HTMLElement;

		if (this.controlNext) {
			this.controlNext.addEventListener('click', this.activateNextIndexCB);
		}
		if (this.controlPrev) {
			this.controlPrev.addEventListener('click', this.activatePrevIndexCB);
		}
		if (this.controlNext || this.controlPrev) {
			this.carouselEl.classList.add(this.carouselClass + '--hasControls');
		}

		this.hammerManager.on('swipe', (event: HammerInput) => {
			event.preventDefault();
			if (event.direction === 2) {
				this.activateNextIndex();
			} else if (event.direction === 4) {
				this.activatePrevIndex();
			}
		});
	}

	protected buildPagination(): void {
		if (this.doPagination && this.slides.length > 1) {
			const hasControls = !!this.controls;

			if (!hasControls && !this.indicatorTray) {
				const indicatorTrayEl: Element = document.createElement('div');
				indicatorTrayEl.classList.add(`${this.carouselClass}__indicators`);
				this.indicatorTray = this.carouselInnerEl.appendChild(indicatorTrayEl) as HTMLElement;
			}
			const indicatorContainer = hasControls ? this.controls : this.indicatorTray;

			// Creates display node
			const indicatorDisplay: Element = document.createElement('span');
			indicatorDisplay.classList.add(`${this.carouselClass}__indicatorDisplay`);
			indicatorDisplay.innerHTML =  `<span class="current">${this.currentIndex + 1}</span> of <span class="total">${this.slides.length}</span>`;

			// Allows for easy access to current
			this.carouselCounterEl = indicatorDisplay.getElementsByClassName('current')[0] as HTMLElement;

			// Creates display node
			indicatorContainer.insertBefore(indicatorDisplay, indicatorContainer.firstChild);

			// NOTE: Needed for transition
			window.setTimeout(() => {
				this.carouselEl.classList.add(this.carouselClass + '--hasIndicators');
			}, 50);
		}
	}

	protected buildIndicators(): void {
		if (this.doIndicators && this.slides.length > 1) {
			if (!this.indicatorTray) {
				const indicatorTrayAbstr: Element = document.createElement('div');
				indicatorTrayAbstr.classList.add(this.carouselClass + '__indicators');

				this.indicatorTray = this.carouselInnerEl.appendChild(indicatorTrayAbstr) as HTMLElement;
			}

			if (this.indicators.length < this.slides.length) {
				const difference: number = this.slides.length - this.indicators.length;
				for (let i: number = this.indicators.length; i < this.slides.length; i++) {
					const indicatorAbstr: Element = document.createElement('div');
					indicatorAbstr.classList.add(this.carouselClass + '__indicator');
					indicatorAbstr.classList.add(`${this.carouselClass}__indicator--${i + 1}`);

					if (!this.indicators.length) {
						indicatorAbstr.classList.add('active');
					}

					const indicator: Element = this.indicatorTray.appendChild(indicatorAbstr);
					this.indicators.push(indicator);
				}
			}

			// NOTE: Needed for transition
			window.setTimeout(() => {
				this.carouselEl.classList.add(this.carouselClass + '--hasIndicators');
			}, 50);
		}
	}

	public activatePrevIndex(): void {
		const newIndex: number = this.prevIndex();
		this.activateByIndex(newIndex);

		if (this.onPrev) {
			this.onPrev();
		}
	}
	public activateNextIndex(): void {
		const newIndex: number = this.nextIndex();
		this.activateByIndex(newIndex);

		if (this.onNext) {
			this.onNext(newIndex);
		}
	}
	public activatePrevIndexCB = (event: Event): void => {
		this.activatePrevIndex();
	}
	public activateNextIndexCB = (event: Event): void => {
		this.activateNextIndex();
	}

	/** Activates slide, indicator, and caption by selected index */
	// NOTE: Number of slides should always equal number of indicators
	public activateByIndex(newIndex: number): void {
		if (this.currentIndex === newIndex) return;

		// Remove existing outgoing classes
		const outEl: NodeList = this.carouselEl.getElementsByClassName('outgoing');
		if (outEl) {
			for (let i: number = outEl.length - 1; i > -1; i--) {
				const el: Element = outEl[i] as Element;
				el.classList.remove('outgoing');
			}
		}

		// Activate slides
		this.slides[newIndex].classList.add('active');
		if (typeof this.currentIndex === 'number') {
			this.slides[this.currentIndex].classList.add('outgoing');
			this.slides[this.currentIndex].classList.remove('active');
		}

		// Activate indicators
		if (this.indicators.length) {
			this.indicators[newIndex].classList.add('active');
			if (typeof this.currentIndex === 'number') {
				this.indicators[this.currentIndex].classList.remove('active');
			}
		} else if (typeof this.carouselCounterEl !== 'undefined') {
			this.carouselCounterEl.innerHTML = (newIndex + 1) as any;
		}

		// Set new current index
		this.priorIndex = this.currentIndex;
		this.currentIndex = newIndex;

		if (this.onChange) {
			this.onChange(newIndex);
		}

		this.initAutoAdvance();
	}

	public deactiveAll(): void {
		const outEl: NodeList = this.carouselEl.getElementsByClassName('outgoing');
		for (let i: number = outEl.length - 1; i > -1; i--) {
			const el: Element = outEl[i] as Element;
			el.classList.remove('outgoing');
		}
		const activeEl: NodeList = this.carouselEl.getElementsByClassName('active');
		for (let i: number = activeEl.length - 1; i > -1; i--) {
			const el: Element = activeEl[i] as Element;
			el.classList.remove('active');
		}

		this.currentIndex = undefined;
	}

	/** Loops around if at start */
	public prevIndex(): number {
		if (this.currentIndex === undefined) {
			return this.slides.length - 1;
		} else {
			return this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
		}
	}

	/** Loops around if at end */
	public nextIndex(): number {
		if (this.currentIndex === undefined) {
			return 0;
		} else {
			return this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
		}
	}

	protected initAutoAdvance(): void {
		if (this.duration) {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			this.timeout = window.setTimeout(() => {
				this.activateNextIndex();
			}, this.duration);
		}
	}
}
